/**
 * The twelve operators: six attackers, six defenders.
 *
 * They deliberately have no names. The name over a body and on the scoreboard
 * is the name the PLAYER typed, and giving the model a second name of its own
 * would put two identities on one soldier — so a card is a designation ("03")
 * and a description of the kit, and nothing else.
 *
 * ## Two palettes each, and why
 *
 * `solo` is the free-for-all look: twelve genuinely different colour families,
 * because in deathmatch everyone is everyone's enemy and telling players apart
 * is the only thing colour has to do.
 *
 * `team` is the bomb-defusal look, and it is the opposite requirement — inside
 * a side the six must read as ONE unit at 40 m, in shadow, moving. So the six
 * reds are all olive-and-yellow and the six blues are all near-black-and-blue,
 * varying only in value and in gear. You still know which operator you picked,
 * because the silhouette differs; your teammate still reads as a teammate,
 * because the colour does not.
 *
 * Nothing here sits below ~0.045 linear (sRGB #3a). Real "black" nylon still
 * bounces 4-6% of the light that lands on it, and an albedo under that crushes
 * every gear surface into a featureless blob in shade.
 */

/**
 * Body and face shape, per operator.
 *
 * Twelve operators who differ only in the colour of their kit are twelve of the
 * same person. These are the numbers that make them twelve people: the torso's
 * width and DEPTH as separate multipliers (a single build scalar only ever
 * makes a bigger version of the same silhouette), limb girth, and four face
 * proportions that survive being masked — cranium width, jaw width, head length
 * and neck.
 *
 * Read `buildSoldierGeometry` for where each lands. The ranges are deliberately
 * narrow — 0.88 to 1.14 — because a soldier outside that stops reading as a
 * person and starts reading as a caricature, and the point is recognition at
 * 40 m, not novelty at 2 m.
 */

/** Shared defaults, so an operator entry states only what it changes. */
const KIT = {
  helmet: true,
  helmetCover: false,
  nvg: false,
  counterweight: false,
  shemagh: false,
  balaclava: false,
  goggles: false,
  beard: false,
  antenna: false,
  kneePads: true,
  elbowPads: true,
  secondary: true,
  holster: true,
  dumpPouch: true,
  shoulderPatch: true,
  magCount: 3,
  build: 1.0,
  heightScale: 1.0,
};

/**
 * Attackers.
 *
 * The team look is olive with a yellow accent: it reads as militia, and nothing
 * on any of the four maps is this yellow, so a moving attacker never blends
 * into cover.
 */
const RED = [
  {
    kit: 'HELMET · COVER · BALACLAVA',
    blurb: 'Covered helmet and a full face wrap. The heaviest plate carrier of '
      + 'the six and the shortest silhouette to go with it.',
    gear: { helmetCover: true, balaclava: true, goggles: true, build: 1.08, heightScale: 0.97, magCount: 4 },
    body: { width: 1.02, depth: 1.12, arm: 1.08, leg: 1.05 },
    face: { width: 1.05, jaw: 1.14, len: 0.96 },
    solo: { camo: ['#4a5a3a', '#5f7049', '#374428', '#788a5c'], gear: '#4c5545', skin: '#8d6a4f', accent: '#7fb04a' },
    team: { camo: ['#4d5220', '#8a8722', '#313613', '#cdbb33'], gear: '#43481b', skin: '#8d6a4f', accent: '#f0cb2c' },
  },
  {
    kit: 'BARE HEAD · SHEMAGH · BEARD',
    blurb: 'No helmet, scarf over the lower face, full beard. Tall and light — '
      + 'the fastest-reading outline in the attacking six.',
    gear: { helmet: false, shemagh: true, beard: true, build: 0.93, heightScale: 1.04, magCount: 2 },
    body: { width: 0.95, depth: 0.90, arm: 0.90, leg: 0.95 },
    face: { width: 0.93, jaw: 0.91, len: 1.08 },
    solo: { camo: ['#8a7a56', '#a3936a', '#6b5d3e', '#bfae80'], gear: '#7d7053', skin: '#a07a5c', accent: '#e0a23c' },
    team: { camo: ['#565c24', '#9a9429', '#383d16', '#dbc93c'], gear: '#4b5120', skin: '#a07a5c', accent: '#ffd93f' },
  },
  {
    kit: 'HELMET · NVG MOUNT · BALACLAVA',
    blurb: 'Night-vision bracket and counterweight still fitted in daylight, '
      + 'radio antenna over the right shoulder.',
    gear: { nvg: true, counterweight: true, balaclava: true, antenna: true, heightScale: 1.01 },
    body: { width: 1.06, depth: 0.98, arm: 1.00, leg: 1.02 },
    face: { width: 1.01, jaw: 0.98, len: 1.02 },
    solo: { camo: ['#59626e', '#6d7784', '#434a54', '#8791a0'], gear: '#535b66', skin: '#795640', accent: '#4aa8c8' },
    team: { camo: ['#454b1c', '#7d7b1f', '#2c3011', '#bdad2e'], gear: '#3e4318', skin: '#795640', accent: '#e6c024' },
  },
  {
    kit: 'HELMET · COVER · GOGGLES · BEARD',
    blurb: 'Goggles pushed up on the helmet cover, beard, dump pouch riding '
      + 'low. The plainest of the six and the easiest to lose track of.',
    gear: { helmetCover: true, goggles: true, beard: true, magCount: 3 },
    body: { width: 1.10, depth: 1.05, arm: 1.05, leg: 0.99 },
    face: { width: 1.09, jaw: 1.05, len: 0.93 },
    solo: { camo: ['#6e4a3c', '#87604c', '#523629', '#a17a60'], gear: '#66493c', skin: '#96704f', accent: '#d9603a' },
    team: { camo: ['#525a26', '#93912a', '#353b17', '#d4c33a'], gear: '#474e1e', skin: '#96704f', accent: '#f5d235' },
  },
  {
    kit: 'BARE HEAD · BALACLAVA · HEAVY',
    blurb: 'No helmet, face fully covered, four magazines and the broadest '
      + 'build in the game. Slow to move behind, hard to miss.',
    gear: { helmet: false, balaclava: true, build: 1.12, heightScale: 1.02, magCount: 4 },
    body: { width: 1.00, depth: 1.14, arm: 1.11, leg: 1.08 },
    face: { width: 1.04, jaw: 1.16, len: 0.95 },
    solo: { camo: ['#3f4348', '#52575d', '#303338', '#666c73'], gear: '#41454a', skin: '#7f6046', accent: '#c8443c' },
    team: { camo: ['#4a5322', '#8b8a24', '#2f3514', '#c7b830'], gear: '#414818', skin: '#7f6046', accent: '#ffcf2e' },
  },
  {
    kit: 'HELMET · SHEMAGH · ANTENNA · SLIM',
    blurb: 'Bare helmet, scarf, long radio whip. The narrowest frame of the '
      + 'six — designed to be hard to hit at range.',
    gear: { shemagh: true, antenna: true, build: 0.9, heightScale: 1.05, magCount: 2 },
    body: { width: 0.91, depth: 0.93, arm: 0.88, leg: 0.92 },
    face: { width: 0.90, jaw: 0.88, len: 1.11 },
    solo: { camo: ['#5a4a63', '#6f5d79', '#43364b', '#8a7793'], gear: '#524659', skin: '#a07a5c', accent: '#b070d8' },
    team: { camo: ['#585f28', '#9d9a2c', '#3b4118', '#e0cf40'], gear: '#4e5522', skin: '#a07a5c', accent: '#ffe04a' },
  },
];

/**
 * Defenders.
 *
 * Near-black with blue panels — police, not army. The value gap from the
 * attackers' olive is deliberate and larger than realism wants: it survives
 * shadow, distance and colour blindness, none of which an armband does.
 */
const BLUE = [
  {
    kit: 'HELMET · COVER · BALACLAVA · GOGGLES',
    blurb: 'Full entry kit: covered helmet, sealed face, goggles down. Nothing '
      + 'of the operator is visible at all.',
    gear: { helmetCover: true, balaclava: true, goggles: true, build: 1.06, magCount: 4 },
    body: { width: 0.97, depth: 1.09, arm: 1.03, leg: 1.04 },
    face: { width: 1.02, jaw: 1.08, len: 0.99 },
    solo: { camo: ['#1d2733', '#2b3a4c', '#141b24', '#3d5268'], gear: '#1b222b', skin: '#8d6a4f', accent: '#3f8fe4' },
    team: { camo: ['#15181d', '#1e2c40', '#0c0e12', '#26436b'], gear: '#101317', skin: '#8d6a4f', accent: '#3f8fe4' },
  },
  {
    kit: 'HELMET · NVG · COUNTERWEIGHT',
    blurb: 'Night-vision arm forward on the helmet, battery pack at the back. '
      + 'Face uncovered below the mount.',
    gear: { nvg: true, counterweight: true, heightScale: 1.02 },
    body: { width: 1.04, depth: 0.96, arm: 0.98, leg: 1.01 },
    face: { width: 0.98, jaw: 0.95, len: 1.06 },
    solo: { camo: ['#1f4a4a', '#2b6060', '#163636', '#3d7d7d'], gear: '#24484a', skin: '#795640', accent: '#6fd0b0' },
    team: { camo: ['#111318', '#24374f', '#0a0c0f', '#2d5080'], gear: '#0d0f13', skin: '#795640', accent: '#57a4f0' },
  },
  {
    kit: 'BARE HEAD · GOGGLES · BEARD · ANTENNA',
    blurb: 'No helmet at all. Goggles on the forehead, beard, radio whip — the '
      + 'one defender you can identify by face.',
    gear: { helmet: false, goggles: true, beard: true, antenna: true, build: 0.94, magCount: 2 },
    body: { width: 0.93, depth: 1.01, arm: 0.94, leg: 0.97 },
    face: { width: 0.96, jaw: 1.02, len: 1.04 },
    solo: { camo: ['#7a4f2e', '#94663e', '#5a3a21', '#ae8055'], gear: '#6d4b2f', skin: '#a07a5c', accent: '#ff8a3c' },
    team: { camo: ['#171b21', '#223349', '#0d1014', '#2a4a75'], gear: '#12161b', skin: '#a07a5c', accent: '#4a97ea' },
  },
  {
    kit: 'HELMET · COVER · BALACLAVA · HEAVY',
    blurb: 'The widest defender: covered helmet, sealed face, four magazines '
      + 'and shoulders to match.',
    gear: { helmetCover: true, balaclava: true, build: 1.14, heightScale: 0.98, magCount: 4 },
    body: { width: 1.08, depth: 1.11, arm: 1.10, leg: 1.06 },
    face: { width: 1.12, jaw: 1.12, len: 0.94 },
    solo: { camo: ['#274a2c', '#356139', '#1b3520', '#4a7d50'], gear: '#24422a', skin: '#7f6046', accent: '#3fd07a' },
    team: { camo: ['#13161b', '#1c2a3d', '#0b0d11', '#234066'], gear: '#0f1216', skin: '#7f6046', accent: '#3585dd' },
  },
  {
    kit: 'BARE HEAD · SHEMAGH · SLIM',
    blurb: 'Scarf, no helmet, no pads on the elbows. The thinnest silhouette '
      + 'on either side and the quietest one to peek with.',
    gear: { helmet: false, shemagh: true, elbowPads: false, build: 0.88, heightScale: 1.06, magCount: 2 },
    body: { width: 0.99, depth: 0.89, arm: 0.89, leg: 0.93 },
    face: { width: 0.91, jaw: 0.93, len: 1.09 },
    solo: { camo: ['#6a2f38', '#84404a', '#4c2028', '#a05c66'], gear: '#5e2e35', skin: '#96704f', accent: '#ff5f7a' },
    team: { camo: ['#191d24', '#26394f', '#0e1115', '#31547f'], gear: '#141820', skin: '#96704f', accent: '#63aefc' },
  },
  {
    kit: 'HELMET · BALACLAVA · ANTENNA · POUCH',
    blurb: 'Bare helmet, sealed face, radio and an oversized dump pouch. The '
      + 'standard-issue look, and the one most often mistaken for a teammate.',
    gear: { balaclava: true, antenna: true, dumpPouch: true, magCount: 3 },
    body: { width: 1.01, depth: 1.03, arm: 1.02, leg: 1.00 },
    face: { width: 1.06, jaw: 1.00, len: 1.00 },
    solo: { camo: ['#8b8b82', '#a3a39a', '#6c6c64', '#bcbcb2'], gear: '#83837a', skin: '#8d6a4f', accent: '#e8d24a' },
    team: { camo: ['#141920', '#203148', '#0a0d11', '#284a78'], gear: '#101419', skin: '#8d6a4f', accent: '#4d9df2' },
  },
];

/** Frozen, resolved catalogue. Index within a side IS the wire value. */
function build(side, list) {
  return list.map((o, i) => ({
    id: `${side}${i + 1}`,
    side,
    index: i,
    /** What a card says instead of a name. */
    label: `0${i + 1}`,
    kit: o.kit,
    blurb: o.blurb,
    gear: { ...KIT, ...o.gear },
    body: o.body,
    face: o.face,
    solo: o.solo,
    team: o.team,
  }));
}

export const OPERATORS = { red: build('red', RED), blue: build('blue', BLUE) };

/** Every operator, attackers first. Deathmatch picks from this. */
export const ALL_OPERATORS = [...OPERATORS.red, ...OPERATORS.blue];

export const OPERATORS_PER_SIDE = OPERATORS.red.length;

/**
 * Resolve a wire index to an operator.
 *
 * In bomb defusal the index is a slot within your own side (0-5); in
 * deathmatch it indexes the flat twelve. A missing or out-of-range value falls
 * back to slot 0 rather than throwing, because a cosmetic choice must never be
 * able to stop somebody spawning.
 */
export function operatorFor(op, { team = 'red', ffa = false } = {}) {
  const n = Number.isFinite(op) ? Math.floor(op) : 0;
  if (ffa) return ALL_OPERATORS[((n % ALL_OPERATORS.length) + ALL_OPERATORS.length) % ALL_OPERATORS.length];
  const side = OPERATORS[team === 'blue' ? 'blue' : 'red'];
  return side[((n % side.length) + side.length) % side.length];
}

/** The palette an operator wears in this mode. See the file header. */
export function paletteFor(operator, ffa) {
  return ffa ? operator.solo : operator.team;
}
